import CampaignListCard from "Components/campaign/CampaignListCard";
import CampaignListFilters from "Components/campaign/CampaignListFilters";
import CampaignPendingTasks from "Components/campaign/CampaignPendingTasks";
import FilterIcon from "Components/icons/FilterIcon";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { Nav, NavItem, NavLink } from "reactstrap";
import { updateCampaignsFilters } from "slices/campaigns/reducer";
import { fetchCampaignsCount, fetchCampaignsList } from "slices/campaigns/thunk";
import { fetchCampaignsActivityThunk } from "slices/campaigns/activity/thunk";
import { setCampaignsLocation } from "slices/ui/reducer";
import { AppDispatch, RootState } from "store";
import { CampaignsActivityFilters } from "types/campaign/activity";
import { CampaignsFilters } from "types/campaign";
import { decodeBase64, encodeBase64, generateUniqueKey } from "utils/helpers";
import "./campaign.scss";
import "./CampaignMilestones.scss";

const STATUS_LIST: Record<string, string> = {
    approved: "Approved",
    completed: "Completed",
    draft: "Draft",
    pending: "Pending",
};

const DEFAULT_STATUS = "approved";

const Campaigns = () => {
    useDocumentTitle("Campaigns");

    const dispatch: AppDispatch = useDispatch();
    const location = useLocation();
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [filtersModalVisibility, showFiltersModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [initialLoading, setInitialLoading] = useState(true);
    const [filterId, setFilterId] = useState<string | null>(null);

    const status = searchParams.get("status") ?? DEFAULT_STATUS;
    const queryFilters = searchParams.get("filters");

    const filters = useSelector((state: RootState) => state.campaigns.filters);
    const campaignsData = useSelector((state: RootState) =>
        filterId ? state.campaigns.filtered_lists[filterId] : undefined
    );
    const campaigns = campaignsData?.list ?? [];
    const page = campaignsData?.page ?? 1;
    const has_more = campaignsData?.has_more ?? true;
    const count = campaignsData?.count ?? 0;

    const loading = useSelector((state: RootState) => state.campaigns.loading);
    const error = useSelector((state: RootState) => state.campaigns.error);
    const totalsFilterKey = `${filters.agency_id || "all"}-${filters.country || "all"}`;
    const totals = useSelector((state: RootState) => state.campaigns.totals[totalsFilterKey]?.data);

    const [activityFilters] = useState<CampaignsActivityFilters>({ page: 1, limit: 1000 });
    const [activityFilterId] = useState(generateUniqueKey(activityFilters, ["page"]));
    const campaignActivityData = useSelector((state: RootState) => state.campaignsActivity.filtered_lists[activityFilterId!]);
    const campaignActivityList = campaignActivityData?.list ?? [];
    const campaignActivityLoading = useSelector((state: RootState) => state.campaignsActivity.loading);

    useEffect(() => {
        const decodedFilters = queryFilters ? decodeBase64(queryFilters) : {};
        dispatch(updateCampaignsFilters({
            page: 1,
            status,
            ...decodedFilters,
        }));
    }, [queryFilters, status, dispatch]);

    useEffect(() => {
        if (filters.page === 1) {
            setInitialLoading(true);
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            dispatch(fetchCampaignsList({ filters })).finally(() => {
                setFilterId(generateUniqueKey(filters, ["page"]));
                setInitialLoading(false);
            });
        }, 500);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [filters, dispatch]);

    useEffect(() => {
        dispatch(fetchCampaignsCount({ agency_id: filters.agency_id, country: filters.country }));
    }, [filters.agency_id, filters.country, dispatch]);

    useEffect(() => {
        dispatch(fetchCampaignsActivityThunk({ filters: activityFilters }));
    }, [dispatch, activityFilters]);

    useEffect(() => {
        setSearchQuery(filters.q ?? "");
    }, [filters.q]);

    useEffect(() => {
        if (location) {
            dispatch(setCampaignsLocation(location));
        }
    }, [location, dispatch]);

    const getTabId = useMemo(() => searchParams.get("status") ?? DEFAULT_STATUS, [searchParams]);

    const setFilters = (updatedFilters: CampaignsFilters) => {
        const { status: nextStatus, page, ...rest } = updatedFilters;
        const cleanedFilters = Object.fromEntries(
            Object.entries(rest).filter(([, value]) => value !== undefined && value !== null && value !== "")
        );
        setSearchParams({
            status: nextStatus,
            ...(Object.keys(cleanedFilters).length > 0 ? { filters: encodeBase64(cleanedFilters) } : {}),
        });
    };

    const onTabClick = (tabId: string) => {
        setFilters({ ...filters, status: tabId, page: 1 });
    };

    const searchCampaign = (q: string) => {
        setSearchQuery(q);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (q.trim().length > 3) {
                setFilters({ ...filters, q: q.trim(), page: 1 });
            } else if (q.trim().length === 0) {
                setFilters({ ...filters, q: "", page: 1 });
            }
        }, 500);
    };

    const nextPage = () => {
        if (has_more && !loading) {
            dispatch(fetchCampaignsList({ filters: { ...filters, page: page + 1 } })).finally(() => {
                setFilterId(generateUniqueKey({ ...filters, page: page + 1 }, ["page"]));
                setInitialLoading(false);
            });
        }
    };

    return (
        <div className="campaign-page">
            <div className="d-flex justify-content-between gap-2 align-items-md-center flex-column flex-md-row">
                <h1 className="campaign-page-title m-0">Campaigns</h1>
                <div className="d-flex gap-2">
                    <div className="campaign-layout-search-container">
                        <span>
                            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.66732 14.0002C11.1651 14.0002 14.0007 11.1646 14.0007 7.66683C14.0007 4.16903 11.1651 1.3335 7.66732 1.3335C4.16951 1.3335 1.33398 4.16903 1.33398 7.66683C1.33398 11.1646 4.16951 14.0002 7.66732 14.0002Z" fill="#BBD8ED" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M15 16L12 13" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => searchCampaign(e.target.value)}
                            className="campaign-layout-search-input"
                        />
                    </div>
                    <button type="button" className="btn btn-white" onClick={() => showFiltersModal(true)}>
                        <FilterIcon className="me-1" /> Filters
                    </button>
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center campaign-page-info mt-4">
                <Nav tabs className="nav nav-tabs nav-tabs-custom nav-primary nav-justified campaign-nav">
                    {Object.entries(STATUS_LIST).map(([statusKey, statusLabel]) => (
                        <NavItem key={statusKey} className="campaign-nav-item">
                            <NavLink
                                style={{ cursor: "pointer" }}
                                className={`campaign-nav-link ${getTabId === statusKey && "active-campaign-nav"} text-start`}
                                onClick={() => onTabClick(statusKey)}
                            >
                                {statusLabel}
                                <span className="count-badge mx-2">{totals?.[statusKey] ?? 0}</span>
                            </NavLink>
                        </NavItem>
                    ))}
                </Nav>
            </div>

            <div className="campaign-page-content my-3">
                {!loading && !initialLoading && campaigns.length > 0 && (
                    <p className="campaign-records-count mb-3">
                        Showing {campaigns.length} of {count} records
                    </p>
                )}
                <InfiniteScroll
                    dataLength={campaigns.length}
                    next={nextPage}
                    className="row g-3 overflow-x-hidden"
                    hasMore={has_more}
                    loader={<></>}
                >
                    {!initialLoading && campaigns.map((campaign, i) => {
                        const campaignActivity = campaignActivityList.find((item) => item.uuid === campaign.uuid);

                        return (
                            <div key={campaign.uuid ?? i} className="campaign-card-outer p-0">
                                <CampaignListCard campaign={campaign} status={filters.status} />
                                {campaignActivity && (
                                    <CampaignPendingTasks
                                        campaignActivity={campaignActivity}
                                        loading={campaignActivityLoading}
                                    />
                                )}
                            </div>
                        );
                    })}
                    {(loading || initialLoading) && Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} height={150} className="w-100" />
                    ))}
                    {!loading && !initialLoading && error && (
                        <div className="d-flex justify-content-center align-items-center">
                            <button type="button" className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>
                                Load More <i className="ms-2 fs-5 bx bx-refresh" />
                            </button>
                        </div>
                    )}
                </InfiniteScroll>
                {!loading && !initialLoading && campaigns.length === 0 && (
                    <div className="d-flex w-100 justify-content-center align-items-center">
                        <div className="length-zero">
                            <h3>Currently there is no campaign in your list</h3>
                        </div>
                    </div>
                )}
            </div>

            <CampaignListFilters
                isOpen={filtersModalVisibility}
                onClose={() => showFiltersModal(false)}
                defaultFilters={filters}
                onApply={(updatedFilters) => setFilters(updatedFilters)}
            />
        </div>
    );
};

export default Campaigns;
